/**
 * Collapsed left navigation: an icon-only rail. Hovering an icon opens a
 * popover with the item name (and its sub-pages, clickable), so the whole
 * menu stays reachable while the sidebar takes ~76px.
 *
 * Keep this file identical in V3/web-frontend and gMVP_command_center/web.
 */
import { HoverCard, NavLink, Stack, Text, ThemeIcon, Tooltip, UnstyledButton } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import type { ComponentType } from "react";

export interface RailItem {
  to: string;
  label: string;
  icon?: ComponentType<{ size?: number | string }>;
  children?: readonly RailItem[];
}

export function NavRail({
  items,
  isActive,
  isChildActive,
}: {
  items: readonly RailItem[];
  /** Whether a top-level item (or any of its sub-pages) is the current page. */
  isActive: (item: RailItem) => boolean;
  /** Whether a sub-page is the current page. */
  isChildActive: (parent: RailItem, child: RailItem) => boolean;
}) {
  return (
    <Stack gap={8} align="center">
      {items.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        const icon = (
          <UnstyledButton
            component={Link}
            to={item.to}
            aria-label={item.label}
            style={{ borderRadius: 12, display: "block" }}
          >
            <ThemeIcon size={44} radius="md" variant={active ? "filled" : "light"}>
              {Icon ? <Icon size={22} /> : <Text fw={700}>{item.label.slice(0, 1)}</Text>}
            </ThemeIcon>
          </UnstyledButton>
        );
        const children = item.children ?? [];
        if (children.length === 0) {
          return (
            <Tooltip key={item.label} label={item.label} position="right" withArrow offset={12}>
              {icon}
            </Tooltip>
          );
        }
        return (
          <HoverCard
            key={item.label}
            position="right-start"
            offset={12}
            openDelay={60}
            closeDelay={150}
            shadow="md"
            withArrow
            withinPortal
          >
            <HoverCard.Target>{icon}</HoverCard.Target>
            <HoverCard.Dropdown p={6} miw={200}>
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" px={10} pt={4} pb={6} style={{ letterSpacing: 1 }}>
                {item.label}
              </Text>
              <Stack gap={2}>
                {children.map((child) => (
                  <NavLink
                    key={child.label}
                    component={Link}
                    to={child.to}
                    // Exact: TanStack's prefix-match aria-current would otherwise
                    // be styled as active by Mantine on every sibling sub-page.
                    activeOptions={{ exact: true }}
                    label={child.label}
                    active={isChildActive(item, child)}
                    styles={{
                      root: { borderRadius: 8, paddingInline: 10, paddingBlock: 6 },
                      label: { fontSize: 14, fontWeight: 600 },
                    }}
                  />
                ))}
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        );
      })}
    </Stack>
  );
}
